import { useEffect, useState } from 'react';
import { api, courses as Api, type Course, type CourseInput, type PubState, type Track } from '@tvx/api-client';
import { Modal } from '../components/Modal';
import { ImagePick } from '../components/ImagePick';

export function CourseForm({ course, onCancel, onSaved }: {
  course: Course | null;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const [f, setF] = useState<CourseInput>(() => course ? {
    slug: course.slug, title: course.title, track: course.track, summary: course.summary,
    cover_media_id: course.cover_media_id, sort: course.sort, state: course.state,
    price: course.price || 0, original_price: course.original_price || 0,
    instructor: course.instructor || 'TradeVix Academy', duration: course.duration || '',
    checkout_url: course.checkout_url || '', highlights: course.highlights || '',
  } : {
    title: '', track: 'Beginner', summary: '', cover_media_id: null, sort: 0, state: 'draft',
    price: 0, original_price: 0, instructor: 'TradeVix Academy', duration: '', checkout_url: '', highlights: '',
  });
  const [cover, setCover] = useState<string | null>(course?.cover_path ?? null);
  const [tracks, setTracks] = useState<Track[]>(['Beginner']);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    void api.get<{ tracks: Track[] }>('/meta').then((m) => m.tracks && setTracks(m.tracks)).catch(() => {});
  }, []);

  const set = <K extends keyof CourseInput>(k: K, v: CourseInput[K]) => setF((p) => ({ ...p, [k]: v }));

  async function submit(state: PubState) {
    if (!f.title.trim()) { setError('A module title is required.'); return; }
    setError('');
    setStatus(state === 'published' ? 'Publishing…' : 'Saving…');
    try {
      if (course) await Api.update(course.id, { ...f, state });
      else await Api.create({ ...f, state });
      await onSaved();
    } catch (e) { setStatus(''); setError((e as Error).message); }
  }

  async function remove() {
    if (!course) return;
    if (!confirm(`Delete “${course.title}”? Its lessons go with it.`)) return;
    try { await Api.remove(course.id); await onSaved(); }
    catch (e) { setError((e as Error).message); }
  }

  return (
    <Modal
      title={course ? `Edit module · ${course.title}` : 'New module'}
      onClose={onCancel}
      footer={
        <>
          <span style={{ fontSize: 12, color: 'var(--tx3)' }}>{status}</span>
          {course ? (
            <button className="b bo" type="button" style={{ marginRight: 'auto' }} onClick={() => void remove()}>Delete</button>
          ) : null}
          <span className="mdl-spacer" />
          <button className="b bo" type="button" onClick={onCancel}>Cancel</button>
          <button className="b bo" type="button" onClick={() => void submit('draft')}>Save draft</button>
          <button className="b bg" type="button" onClick={() => void submit('published')}>
            {course?.state === 'published' ? 'Save' : 'Publish'}
          </button>
        </>
      }
    >
      {error ? <div className="err">{error}</div> : null}

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 100px', gap: 14 }}>
        <div className="fld"><label className="lbl">Title</label>
          <input className="inp" value={f.title} autoFocus onChange={(e) => set('title', e.target.value)} /></div>
        <div className="fld"><label className="lbl">Track</label>
          <select className="inp" value={f.track} onChange={(e) => set('track', e.target.value as Track)}>
            {tracks.map((t) => <option key={t}>{t}</option>)}
          </select></div>
        <div className="fld"><label className="lbl">Order</label>
          <input className="inp" type="number" value={f.sort} onChange={(e) => set('sort', Number(e.target.value))} /></div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 14, marginTop: 14 }}>
        <div className="fld"><label className="lbl">Price (₹)</label>
          <input className="inp" type="number" value={f.price || 0} onChange={(e) => set('price', Number(e.target.value))} /></div>
        <div className="fld"><label className="lbl">Original Price (₹)</label>
          <input className="inp" type="number" value={f.original_price || 0} onChange={(e) => set('original_price', Number(e.target.value))} /></div>
        <div className="fld"><label className="lbl">Instructor</label>
          <input className="inp" value={f.instructor || ''} placeholder="TradeVix Academy" onChange={(e) => set('instructor', e.target.value)} /></div>
        <div className="fld"><label className="lbl">Duration</label>
          <input className="inp" value={f.duration || ''} placeholder="e.g. 6 Hours" onChange={(e) => set('duration', e.target.value)} /></div>
      </div>

      <div className="fld" style={{ marginTop: 14 }}><label className="lbl">Payment link (optional)</label>
        <input className="inp" value={f.checkout_url || ''} placeholder="https://…" onChange={(e) => set('checkout_url', e.target.value)} />
        <p className="hint">
          Where a buyer is sent to pay. Without one, checkout falls back to the default payment link in
          Settings. Original price is shown struck out only when it is higher than Price; leave Price at 0
          and the course is not offered for sale at all.
        </p>
      </div>

      <div style={{ marginTop: 14 }}>
        <ImagePick label="Module thumbnail" path={cover} alt={f.title}
          hint="Shown on the module card, on the website and in the member area. The whole picture is shown — nothing is cropped — and a 16:9 landscape fills the card exactly."
          onPick={(id, p) => { set('cover_media_id', id); setCover(p); }} />
      </div>

      <div className="fld" style={{ marginTop: 14 }}><label className="lbl">What you'll learn (one point per line)</label>
        <textarea className="inp ta" rows={6} value={f.highlights || ''} placeholder={'How to read price and time together\nSpotting reversals before they print\nA checklist to plan every trade'}
          onChange={(e) => set('highlights', e.target.value)} />
        <p className="hint">Shown as bullet points above the curriculum on the website and in the app. Up to 20 lines; leave empty to hide the section.</p>
      </div>

      <div className="fld" style={{ marginTop: 14 }}><label className="lbl">Summary</label>
        <textarea className="inp ta" rows={3} value={f.summary} onChange={(e) => set('summary', e.target.value)} />
        <p className="hint">Shown on the module card and used as the course description a search engine reads.</p>
      </div>
    </Modal>
  );
}
